import { useMutation } from '@tanstack/react-query'
import { customQueryClient } from '../../config/query-client'
import { notificationsMutationKeys, notificationsQueryKeys } from '../constants'
import api from '../../config/axios-config'

/**
 * Marks all notifications as read up to a timestamp (inclusive).
 * @param {Object} options - Additional options for `useMutation`.
 * @returns {Object} - Mutation result. Call `mutate({ timestamp })` with an ISO-8601 UTC instant.
 * Endpoint:
 * Service: POST /notifications-api/v1/notifications/all?timestamp=<ISO-UTC>
 * Frontend proxy: POST /api/notifications/all?timestamp=<ISO-UTC>
 * Mutation payload:
 * `timestamp`: ISO-8601 instant in UTC. The server marks as read all notifications created before or at this time.
 * Typical UI flow:
 * Trigger this action from "Mark all as read" in the notifications panel.
 * After success, refresh list/unread state to keep badge and list aligned.
 * Tip:
 * To mark everything currently visible as read, use the newest visible `created` timestamp or the latest `ETag`.
 * Ensure the value is a valid UTC instant, e.g. `2025-11-19T14:03:12Z`.
 */

export const useNotificationsReadAllMutation = (options) => {
  const handleMutation = async ({ timestamp }) => {
    const config = {
      method: 'post',
      url: '/api/notifications/all',
      params: { timestamp }
    }

    const { data } = await api(`/api/notifications/all`, config)

    return data
  }

  const mergedOptions = { ...options }
  const customOnSuccess = options?.onSuccess

  delete mergedOptions.onSuccess

  return useMutation({
    mutationKey: [notificationsMutationKeys.NOTIFICATIONS_READ_ALL],
    mutationFn: handleMutation,
    onSuccess: (data, variables, context) => {
      customQueryClient.invalidateQueries({
        queryKey: [notificationsQueryKeys.NOTIFICATIONS_UNREAD]
      })

      customQueryClient.removeQueries({
        queryKey: [notificationsQueryKeys.NOTIFICATIONS_GET_ALL]
      })

      customQueryClient.invalidateQueries({
        queryKey: [notificationsQueryKeys.NOTIFICATIONS_GET_ALL]
      })

      customOnSuccess?.(data, variables, context)
    },
    ...mergedOptions
  })
}
